import { Card } from '../../../../shared/ui/Card.js';
import { Button } from '../../../../shared/ui/Button.js';
import type { CashPaymentStartResponseDTO } from '../../../checkout/checkoutApi.js';

export interface PhoneFirstCheckoutCashCollectStepViewProps {
  readonly t: (key: string, options?: Record<string, unknown>) => string;
  readonly cashCompleteResult: CashPaymentStartResponseDTO;
  readonly orderNumber: string | null;
  readonly kioskName: string;
  readonly tenantDisplayName: string;
  readonly cashPaymentConfirmed: boolean;
  readonly allowContinueWithoutPaid: boolean;
  readonly onContinue: () => void;
}

function formatAmountMajor(amountMinor: number): string {
  return (amountMinor / 100).toFixed(2);
}

export function PhoneFirstCheckoutCashCollectStepView(
  props: PhoneFirstCheckoutCashCollectStepViewProps,
): JSX.Element {
  const {
    t,
    cashCompleteResult,
    orderNumber,
    kioskName,
    tenantDisplayName,
    cashPaymentConfirmed,
    allowContinueWithoutPaid,
    onContinue,
  } = props;
  const amountMajor = formatAmountMajor(cashCompleteResult.amountMinor);
  const awaitingStaff =
    cashCompleteResult.awaitingCashConfirmation === true &&
    !cashPaymentConfirmed &&
    !allowContinueWithoutPaid;
  const showContinue = cashPaymentConfirmed || allowContinueWithoutPaid;

  let bodyCopy: JSX.Element;
  if (cashPaymentConfirmed) {
    bodyCopy = (
      <p
        className="text-sm font-medium text-[var(--color-success,#15803d)]"
        data-testid="customer-cash-paid-banner"
      >
        {t('checkout.cashCollect.paidBanner')}
      </p>
    );
  } else if (awaitingStaff) {
    bodyCopy = (
      <>
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('checkout.cashCollect.handToStaff')}
        </p>
        <p className="text-sm text-[var(--color-on-surface-muted)]" data-testid="customer-cash-waiting">
          {t('checkout.cashCollect.waitingForStaff')}
        </p>
      </>
    );
  } else {
    bodyCopy = (
      <>
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('checkout.cashCollect.instructions', {
            kioskName,
            amount: amountMajor,
            currency: cashCompleteResult.currency,
          })}
        </p>
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('checkout.cashCollect.thanksContinue')}
        </p>
      </>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="customer-cash-collect-step">
      <Card className="flex flex-col gap-3 p-4">
        <h2 className="text-lg font-semibold text-[var(--color-on-surface)]">
          {cashPaymentConfirmed
            ? t('checkout.cashCollect.paidTitle')
            : t('checkout.cashCollect.waitingTitle')}
        </h2>
        <p
          className="text-2xl font-semibold tabular-nums text-[var(--color-on-surface)]"
          data-testid="customer-cash-collect-amount"
        >
          {amountMajor} {cashCompleteResult.currency}
        </p>
        {orderNumber !== null ? (
          <p className="text-sm text-[var(--color-on-surface-muted)]" data-testid="customer-cash-order-id">
            {t('checkout.cashCollect.orderNumber', {
              orderId: orderNumber,
            })}
          </p>
        ) : (
          <p
            className="text-sm text-[var(--color-on-surface-muted)]"
            data-testid="customer-cash-order-id-pending"
          >
            {t('checkout.cashCollect.orderNumberPending')}
          </p>
        )}
        <p className="text-sm text-[var(--color-on-surface-muted)]">
          {t('checkout.cashCollect.venue', { tenantName: tenantDisplayName, kioskName })}
        </p>
        {bodyCopy}
      </Card>
      {showContinue ? (
        <Button intent="primary" size="lg" block type="button" onClick={onContinue}>
          {t('checkout.success.backToShop')}
        </Button>
      ) : null}
    </div>
  );
}
